import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../Api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Role display mapping
  const getRoleDisplayName = (role) => {
    const roleMap = {
      'administrateur_general_superieur': 'Business Officer',
      'administrateur_general': 'Administrateur Général',
      'resp_admin': 'Responsable Administratif',
      'resp_admin_superieur': 'Responsable Administratif Supérieur',
      'chef_terrain': 'Chef de Terrain',
      'chef_terrain_superieur': 'Head of operations',
      'charge_etude': 'Chargé d\'Étude',
      'charge_etude_superieur': 'Chargé d\'Étude Supérieur',
      'resp_cati': 'Responsable CATI',
      'resp_cati_superieur': 'Responsable CATI Supérieur',
      'supervisor_cati': 'Superviseur CATI',
      'resp_controle': 'Responsable Contrôle',
      'superviseur': 'Prestataire',
      'controleur': 'Contrôleur',
      'enqueteur': 'Enquêteur'
    };
    return roleMap[role] || role;
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      const userData = response.data.user;
      // Add role_display if not present
      if (userData && !userData.role_display) {
        userData.role_display = getRoleDisplayName(userData.role);
      }
      setUser(userData);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, token) => {
    localStorage.setItem('auth_token', token);
    // Add role_display if not present
    if (userData && !userData.role_display) {
      userData.role_display = getRoleDisplayName(userData.role);
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser: checkAuthStatus,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
